import { call, take, takeLatest, race } from 'redux-saga/effects';
import { Rpc } from '@dynamika/rpc-core/contracts';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  ExecutionType,
  RunProcessType,
} from '@dynamika-shared/types/executions';
import { resolveFormCall, rejectFormCall } from 'src/services/rpc/pendingCalls';
import { DICOMViewerProcessesSlice } from './DICOMViewerProcesses.slice';
import { runToast } from '../tools';
import { executionsLongPollingWorker } from '../QCTaskExecutions/QCTaskExecutions.saga';
import {
  runQCTExport,
  runTaskExecution,
  runTaskExecutionExportRun,
  runTaskExecutionImportInit,
  runTaskExecutionImportRun,
  runTaskExecutionImportSendFile,
} from 'src/api/qcProcesses';
import { Resp } from '@dynamika-shared/types/general';

export function* runProcessWorker(
  action: PayloadAction<RunProcessType & { __callId?: string }>
) {
  const workflowType = action.payload.workflowType;
  switch (workflowType) {
    case 'QC_IMPORT':
      yield call(importSegmentationProcessWorker, action.payload);
      break;
    case 'QC_EXPORT':
      yield call(runProcessExportWorker, action.payload);
      break;
    case ExecutionType.QCT:
      yield call(runQCTProcessWorker, action.payload);
      break;
    default:
      yield call(defaultRunProcessWorker, action.payload);
      break;
  }
}

export function* importSegmentationProcessWorker({
  taskId,
  processName,
  clinicalTrialId,
  clinicalTrialInstrumentId,
  data,
  __callId,
}: RunProcessType & { __callId?: string }) {
  try {
    const {
      data: { signedUrl, executionFileId },
    }: Resp<typeof runTaskExecutionImportInit> = yield call(
      runTaskExecutionImportInit,
      taskId,
      clinicalTrialId,
      clinicalTrialInstrumentId
    );

    if (!data) {
      throw new Error('No data');
    }

    const { attachments } = data as { attachments?: File[] };

    const file = attachments?.[0];

    if (!file) {
      throw new Error('No file');
    }

    yield call(
      runTaskExecutionImportSendFile,
      file,
      signedUrl,
      'application/zip'
    );

    yield call(
      runTaskExecutionImportRun,
      taskId,
      clinicalTrialId,
      clinicalTrialInstrumentId,
      executionFileId,
      data
    );

    yield runToast('info', `${processName} process has started`);
    resolveFormCall(__callId, null);
  } catch (error) {
    yield call(runToast, 'error', 'Error importing segmentation');
    rejectFormCall(__callId, error);
  }
}

export function* defaultRunProcessWorker(
  payload: RunProcessType & { __callId?: string }
) {
  try {
    const {
      clinicalTrialId,
      data,
      processName,
      taskId,
      clinicalTrialInstrumentId,
    } = payload;
    yield call(
      runTaskExecution,
      taskId,
      clinicalTrialId,
      clinicalTrialInstrumentId,
      data
    );
    yield runToast('info', `${processName} process has started`);
    resolveFormCall(payload.__callId, null);
  } catch (error) {
    rejectFormCall(payload.__callId, error);
  }
}

/**
 * "Send to QCT" — the viewer's Processes menu (D7-5232).
 *
 * Plain terms
 * -----------
 * Same flow as any other process the specialist starts from the viewer: the
 * selected series go out and a toast confirms it. Only the destination differs.
 *
 * Technical
 * ---------
 * Identical to `defaultRunProcessWorker` except for the endpoint — QCT does not
 * use the generic `POST /qc/tasks/:taskId/executions` but its own
 * `executions/qct/export`. No long polling here, unlike `QC_EXPORT`: nothing
 * comes back for the viewer to download, the results arrive later through the
 * QC page's workstation query.
 */
export function* runQCTProcessWorker(
  payload: RunProcessType & { __callId?: string }
) {
  try {
    const {
      clinicalTrialId,
      data,
      processName,
      taskId,
      clinicalTrialInstrumentId,
    } = payload;

    yield call(
      runQCTExport,
      taskId,
      clinicalTrialId,
      clinicalTrialInstrumentId,
      data
    );

    yield runToast('info', `${processName} process has started`);

    resolveFormCall(payload.__callId, null);
  } catch (error) {
    yield call(runToast, 'error', 'Error sending series to QCT');

    rejectFormCall(payload.__callId, error);
  }
}

export function* runProcessExportWorker(
  payload: RunProcessType & { __callId?: string }
) {
  try {
    const {
      clinicalTrialId,
      data,
      processName,
      taskId,
      clinicalTrialInstrumentId,
    } = payload;
    yield call(
      runTaskExecutionExportRun,
      taskId,
      clinicalTrialId,
      clinicalTrialInstrumentId,
      data
    );
    yield call(runToast, 'info', `${processName} process has started`);
    // Interrupt long polling if user closes viewer
    const { cancelled } = yield race({
      done: call(executionsLongPollingWorker),
      cancelled: take([Rpc.Events.Viewer.CLOSED]),
    });

    if (cancelled) {
      // Stop further handling to avoid using a different patient's context
      resolveFormCall(payload.__callId, null);
      return;
    }

    resolveFormCall(payload.__callId, null);
  } catch (error) {
    yield call(runToast, 'error', 'Error exporting DICOM file');
    rejectFormCall(payload.__callId, error);
  }
}

export function* DICOMViewerProcessesSaga() {
  yield takeLatest(DICOMViewerProcessesSlice.actions.runProcess, runProcessWorker);
}
